import {
  useBreadcrumbJsonLd,
} from './useBreadcrumbJsonLd';
import {
  useJsonLdShared,
  type BreadcrumbCrumb,
  type PageSeoData,
} from './useJsonLdShared';
import { useSiteManifestStore } from '~/app/stores/siteManifest';

/**
 * Section homes that are real pages (audit). b2b / b2c / initiatives excluded for now.
 * Keys are first path segments after the locale.
 */
const SECTION_CRUMBS: Record<string, string> = {
  'about-us': 'About Us',
  'about-food-waste': 'About Food Waste',
  careers: 'Careers',
  legal: 'Legal',
  'surplus-food-marketplace': 'Surprise Bags',
};

/**
 * BreadcrumbList for CMS pages from pageBySlug / index-style fetches.
 * Flat: Home > [Page]. Section child: Home > [Section] > [Page].
 */
export function useCmsPageBreadcrumbJsonLd(pageData: PageSeoData) {
  const { t } = useI18n();
  const { localePath, baseUrl } = useJsonLdShared();
  const siteManifestStore = useSiteManifestStore();

  const page = computed(() => unref(pageData)?.page);

  const pageUrl = computed(() => {
    const slug = page.value?.slug;
    if (typeof slug !== 'string' || !slug.trim()) {
      return null;
    }
    const path = slug.startsWith('/') ? slug : `/${slug}`;
    return `${baseUrl.value}${localePath(path)}`;
  });

  const crumbs = computed((): BreadcrumbCrumb[] | null => {
    const current = page.value;
    const url = pageUrl.value;
    if (!current || !url) {
      return null;
    }

    const slug = typeof current.slug === 'string' ? current.slug : '';
    const parts = slug.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);
    if (parts.length === 0) {
      return null;
    }

    const title = (
      current.seoSocial?.metaTitle ||
      current.title ||
      parts[parts.length - 1] ||
      ''
    ).trim();
    if (!title) {
      return null;
    }

    const homepageUrl = `${baseUrl.value}${localePath('/')}`;
    const home: BreadcrumbCrumb = { name: t('home'), item: homepageUrl };
    const leaf: BreadcrumbCrumb = { name: title, item: url };

    if (parts.length === 1) {
      return [home, leaf];
    }

    const sectionKey = parts[0];
    const blogSlug = siteManifestStore.slugs?.blog || 'blog';
    const pressSlug = siteManifestStore.slugs?.pressReleases || 'press';
    const careersSlug = siteManifestStore.slugs?.careers || 'careers';

    const sectionName =
      SECTION_CRUMBS[sectionKey] ||
      (sectionKey === blogSlug ? 'Blog' : undefined) ||
      (sectionKey === pressSlug ? 'Press' : undefined) ||
      (sectionKey === careersSlug ? 'Careers' : undefined);

    // Unknown / deferred sections (e.g. b2b): Home > Page only — no broken intermediate item.
    if (!sectionName) {
      return [home, leaf];
    }

    return [
      home,
      {
        name: sectionName,
        item: `${baseUrl.value}${localePath(`/${sectionKey}`)}`,
      },
      leaf,
    ];
  });

  useBreadcrumbJsonLd(crumbs, pageUrl, 'cms-breadcrumb-jsonld');
}
